// ts-rest contract for the book assignment surface. Ops, Comply and Admin reach
// the roster reads, `assign` and `clear`. `transfer` narrows to Ops and Admin,
// because a whole-book handover repoints the grid manager aliases through two
// Actions that refuse a comply actor (see the controller header).
//
// The reads are FIRM-WIDE and carry no `ctx` row scope, so the route persona is
// the only gate on them. The `/api/admin/...` path is retained for continuity
// and no longer means "admin only".
//
// WHY THIS EXISTS. Who holds a client is derived, and it is not entered: the
// ValuationGrid names a manager for each Portfolio, `manager_aliases` maps that
// string onto a `users` row, an Account belongs to the books of the managers of
// its Portfolios, and a Client belongs to the books of the managers of their
// Accounts. Nothing in the product lets a person say who holds a client. That
// leaves two gaps the firm hits in practice — a newly joined IM reads an empty
// book until the book-of-record names them, and a leaver's book cannot move to
// the successor at all.
//
// A CLIENT CAN SIT IN TWO BOOKS. The rule takes no vote, so a Client whose
// Accounts hold Portfolios under two managers belongs to both books
// (ADR-0005, `lib/desk/client-book-membership.ts`). Every book field on this
// surface is therefore a LIST and never a scalar. The stored column
// `Client.investmentManagerUserId` cannot carry that answer: it names the book
// of a Client that no Account reaches, and it governs nothing for every other
// Client.
//
// This surface closes both. `assign` stamps the IM by hand through
// `assign-client-book`; `transfer` moves a whole book from one IM to another
// and can repoint the leaver's grid manager aliases in the same step, so new
// portfolios the grid still labels with the leaver land in the successor's
// book. `clear` hands a client back to the derivation.
//
// A manual assignment is a STOPGAP, not a new source of truth. An alias
// re-attribution skips an assigned row — that backfill is firm-wide and runs on
// every alias write, so without the skip one edit for an unrelated IM would
// silently revert every hand assignment. The next ValuationGrid upload re-derives
// the attribution and clears the marker, because an upload brings real news.
// Book Health lists the open assignments so a row the upload will reclaim is
// visible rather than silent.
//
// Endpoints under /api/admin/client-books:
//   GET  /ims       — the book-owning IM roster with live book sizes
//   GET  /clients   — the client roster, filtered by book, by name or by id
//   POST /assign    — assign one or more clients to an IM (or to nobody)
//   POST /clear     — hand clients back to the grid derivation
//   POST /transfer  — move a whole book, optionally with the grid aliases

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { errorBodyWithCode as errorBody } from '../schemas/common';

const c = initContract();

// ---------------------------------------------------------------------------
// Data shapes
// ---------------------------------------------------------------------------

/**
 * A row in the book picker. It is normally a book-owning IM — an Active desk
 * user who is not somebody's assistant. It is ALSO any user who holds clients
 * today without being one, because that is what a leaver looks like once the
 * directory deactivates them, and an admin who cannot see that book cannot move
 * it. `canReceive` separates the two: only a book owner is a valid target.
 */
export const clientBookImSchema = z.object({
    userId: z.string(),
    name: z.string().nullable(),
    email: z.string(),
    /** False for a holder who owns no book — an assistant, a non-desk user, or
     *  a deactivated account. Their clients are listable and movable AWAY, but
     *  nothing may be assigned TO them. */
    canReceive: z.boolean(),
    /** Clients in this IM's book right now, by the derivation. A Client in two
     *  books counts in both, so the counts across the roster can add up to more
     *  than the number of clients the firm holds. */
    clientCount: z.number(),
    /** Of those, the ones an admin assigned by hand. An assigned Client sits in
     *  exactly one book, because the marker gives the stored column the last
     *  word for that Client. */
    assignedCount: z.number(),
    /** The grid manager strings bound to this IM. An IM with none can never
     *  gain a client from an upload, which is what a new joiner looks like. */
    aliases: z.array(z.string()),
});
export type ClientBookIm = z.infer<typeof clientBookImSchema>;

/** One book that holds a client, as the picker names it. */
export const clientBookHolderSchema = z.object({
    userId: z.string(),
    /** The directory name of the manager, and their email where the directory
     *  holds no name — the same label the IM roster shows. */
    name: z.string(),
});
export type ClientBookHolder = z.infer<typeof clientBookHolderSchema>;

/** One client row in the assignment picker. */
export const clientBookClientSchema = z.object({
    clientId: z.string(),
    name: z.string(),
    /** EVERY book that holds this client. Normally one. Empty when no book
     *  holds them. Two when their Accounts hold Portfolios under two managers,
     *  which the rule admits without a vote — the operator must see both books
     *  before they move the client out of them. */
    ims: z.array(clientBookHolderSchema),
    /** The manager string the grid carries for this person, for the operator to
     *  sanity-check the move against the book-of-record. Null when the grid
     *  holds no portfolio for them (a manually registered client). */
    gridManager: z.string().nullable(),
    /** Set when an admin assigned this client by hand; null when the grid rule
     *  decides the row. */
    assignedAt: z.string().nullable(),
    assignedByName: z.string().nullable(),
});
export type ClientBookClient = z.infer<typeof clientBookClientSchema>;

export const listClientBookImsResponseSchema = z.object({
    ims: z.array(clientBookImSchema),
    /** Clients that no book holds. They are reachable in the picker through
     *  `imUserId: 'unassigned'`. */
    unassignedCount: z.number(),
});
export type ListClientBookImsResponse = z.infer<typeof listClientBookImsResponseSchema>;

export const listClientBookClientsResponseSchema = z.object({
    clients: z.array(clientBookClientSchema),
    /** Matches before the page limit, so the UI can say what it is not showing. */
    total: z.number(),
});
export type ListClientBookClientsResponse = z.infer<typeof listClientBookClientsResponseSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const assignClientBookRequestSchema = z.object({
    clientIds: z.array(z.string().min(1)).min(1).max(2000),
    /** Null detaches the clients from every book. */
    imUserId: z.string().min(1).nullable(),
    reason: z.string().max(500).optional(),
});
export type AssignClientBookRequest = z.infer<typeof assignClientBookRequestSchema>;

export const clearClientBookRequestSchema = z.object({
    clientIds: z.array(z.string().min(1)).min(1).max(2000),
});
export type ClearClientBookRequest = z.infer<typeof clearClientBookRequestSchema>;

export const transferClientBookRequestSchema = z.object({
    fromImUserId: z.string().min(1),
    toImUserId: z.string().min(1),
    /** Also repoint the leaver's grid manager aliases onto the successor, so a
     *  later upload puts new portfolios in the successor's book. Untick it when
     *  the firm is correcting the book-of-record itself. */
    moveAliases: z.boolean(),
    reason: z.string().max(500).optional(),
});
export type TransferClientBookRequest = z.infer<typeof transferClientBookRequestSchema>;

export const clientBookMutationResponseSchema = z.object({
    ok: z.literal(true),
    /** Clients whose book changed. */
    changed: z.number(),
    /** Grid manager aliases repointed onto the successor (transfer only). */
    aliasesMoved: z.array(z.string()),
    /** Aliases the transfer could NOT move, with the reason. The client moves
     *  regardless: a refused alias must not fail a book handover. */
    aliasesSkipped: z.array(z.object({ alias: z.string(), reason: z.string() })),
});
export type ClientBookMutationResponse = z.infer<typeof clientBookMutationResponseSchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const clientBooksContract = c.router(
    {
        listIms: {
            method: 'GET',
            path: '/admin/client-books/ims',
            responses: { 200: listClientBookImsResponseSchema },
            summary: 'The book-owning IM roster with live book sizes and grid aliases',
        },
        listClients: {
            method: 'GET',
            path: '/admin/client-books/clients',
            query: z.object({
                /** A `users` id, the literal `unassigned`, or absent for every client. */
                imUserId: z.string().optional(),
                /** Case-insensitive substring of the client name. */
                q: z.string().optional(),
                /** One client id. The client record reads its own row with it,
                 *  so the move dialog names exactly one client (SEP-302). */
                clientId: z.string().min(1).optional(),
                limit: z.coerce.number().int().min(1).max(500).optional(),
            }),
            responses: { 200: listClientBookClientsResponseSchema },
            summary: 'Clients filtered by book and by name, for the assignment picker',
        },
        assign: {
            method: 'POST',
            path: '/admin/client-books/assign',
            body: assignClientBookRequestSchema,
            responses: { 200: clientBookMutationResponseSchema, 400: errorBody, 404: errorBody },
            summary: "Assign clients to an IM's book by hand",
        },
        clear: {
            method: 'POST',
            path: '/admin/client-books/clear',
            body: clearClientBookRequestSchema,
            responses: { 200: clientBookMutationResponseSchema, 400: errorBody },
            summary: 'Hand manually assigned clients back to the grid derivation',
        },
        transfer: {
            method: 'POST',
            path: '/admin/client-books/transfer',
            body: transferClientBookRequestSchema,
            responses: { 200: clientBookMutationResponseSchema, 400: errorBody, 404: errorBody },
            summary: 'Move a whole book to another IM, optionally with the grid aliases',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
